import localforage from 'localforage';
import * as Sentry from '@sentry/react';

import { MUTATION_QUEUE_STORAGE_KEY } from '../offline/offlineQueue';
import { COMPOUND_QUEUE_STORAGE_KEY } from '../offline/offlineCompoundQueue';
import {
  IMAGE_UPLOAD_QUEUE_STORAGE_KEY,
  OFFLINE_PENDING_UPLOADS_DIR,
  sweepOrphanPendingUploads,
} from '../offline/offlineImageUploadQueue';
import { clearLostImageNotices } from '../offline/lostImageNotices';
import { pruneSyncedGpsTracks } from '../offline/gpsTrackStore';
import { PAGE_SIZE_STORAGE_PREFIX } from '../hooks/usePersistedPageSize';
import { deleteAllOpfsEntries } from './files';
import { clearOfflineSession } from './offlineSession';
import { LAST_OWNER_KEY } from '../offline/queueOwner';

/**
 * Claves de `sessionStorage` que SOBREVIVEN al cierre de sesión. Todo lo demás
 * se borra.
 *
 * Son marcas técnicas del navegador, no datos de nadie:
 * - `debug`: los namespaces de log que activa el paquete `debug`.
 * - `rois_version_modal_recent_fire`: el centinela anti-doble aviso de versión
 *   (`VERSION_MODAL_RECENT_FIRE_KEY` en `versionManager.ts`).
 * - `rois_last_queue_owner`: el último dueño de la cola (ver abajo).
 * - `rois:orphan-sw-cleaned`: la marca que impide el bucle de recargas al
 *   retirar un service worker huérfano en el APK (`main.tsx`).
 * - Las de Sentry (`sentry*`), que guardan su propia sesión de grabación.
 *
 * 🔴 Es una lista de CONSERVAR a propósito, no de borrar. Antes el logout
 * borraba listas de claves escritas a mano y se quedaron viejas: ~15 familias
 * sobrevivían (vehículos excluidos, borradores de gestión, `ot-modal`…).
 * Una clave nueva queda cubierta sin acordarse de nada.
 */
export const SESSION_KEYS_KEPT_ON_LOGOUT: readonly string[] = [
  'debug',
  'rois_version_modal_recent_fire',
  'rois:orphan-sw-cleaned',
  // 🔴 El último dueño conocido de la cola (`offline/queueOwner.ts`, #331).
  // Sella lo que se termina de guardar justo DESPUÉS del logout con el usuario
  // que lo pidió. Si se borrara, esa operación quedaría SIN dueño, y una sin
  // dueño la envía cualquiera: el siguiente operario la mandaría con su token y
  // a su tenant — la fuga que #331 cierra. No es un dato de nadie: solo
  // `userId` + `tenantId` del que acaba de salir.
  LAST_OWNER_KEY,
];

/**
 * Claves de `localforage` que SOBREVIVEN al cierre de sesión: las tres colas sin
 * conexión (spec 2026-10-12). Todo lo demás de la instancia se borra.
 */
export const QUEUE_KEYS_KEPT_ON_LOGOUT: readonly string[] = [
  MUTATION_QUEUE_STORAGE_KEY,
  COMPOUND_QUEUE_STORAGE_KEY,
  IMAGE_UPLOAD_QUEUE_STORAGE_KEY,
];

const keptOnLogout = (key: string) =>
  SESSION_KEYS_KEPT_ON_LOGOUT.includes(key) || key.startsWith('sentry');

const report = (step: string) => (error: unknown) =>
  Sentry.captureException(error, { tags: { feature: 'logout', step } });

/**
 * Borra del dispositivo todo lo que pertenece al usuario que cierra sesión.
 *
 * Por BARRIDO y no por listas: la instancia por defecto de localforage
 * entera, toda la raíz de OPFS y todo `sessionStorage` salvo las marcas de
 * arriba. Las listas a mano se quedaban viejas en cuanto alguien añadía un
 * almacén nuevo — así sobrevivieron las fotos de la bandeja y los avisos de
 * fotos perdidas.
 *
 * Lo que NO toca, y por qué:
 * - 🔴 El TRABAJO SIN ENVIAR (spec 2026-10-12): las tres colas, las fotos que
 *   esperan en `offline-pending-uploads` y los puntos GPS sin subir. Desde la
 *   #331 cada operación lleva dueño y solo la envía él: si vuelve a entrar, sale
 *   sola; si entra otro, le aparece como «de otro usuario» y no se envía.
 *   Borrarlas al salir solo perdía trabajo de campo. Lo que no tenía dueño lo
 *   sella el logout antes de llegar aquí (`sealUnownedQueuedWork`). De la
 *   carpeta de fotos solo se van los ficheros sueltos, que ya no son de nadie.
 * - CacheStorage: solo contiene la precaché de la app (no hay
 *   `runtimeCaching`). Vaciarla dejaba la web sin arranque offline hasta el
 *   siguiente despliegue, porque Workbox no la repara sin `integrity` (medido
 *   2026-10-08: 88 → 0 entradas y `ERR_FAILED` sin red).
 * - Las preferencias del dispositivo en `localStorage` (idioma, tema, cámara,
 *   la escalera del aviso de versión): no son de un usuario.
 * - La semilla de Apollo, la malla de PK y la sesión nativa: las borra el
 *   logout aparte porque dependen de la identidad, que aquí ya no existe.
 *
 * Nunca lanza: cada paso registra su fallo y deja seguir a los demás. Cerrar
 * la sesión es el contrato, y un almacén que no se deja borrar no puede
 * dejarla abierta.
 */
export async function purgeUserStorage(): Promise<void> {
  // ── IndexedDB (instancia por defecto de localforage) ──────────────────────
  // Los avisos de fotos perdidas, con su `clear*` (avisa a la interfaz: el
  // aviso desaparece al momento). Del rastro GPS (su propia instancia,
  // `rois/gps_local_track`) solo las rutas ya subidas. Y de las fotos que
  // esperan a subir, solo los ficheros sueltos: los que ninguna entrada de la
  // cola usa (`sweepOrphanPendingUploads`, con sus salvaguardas).
  await Promise.allSettled([
    clearLostImageNotices(),
    pruneSyncedGpsTracks(),
    sweepOrphanPendingUploads(),
  ]).then((results) =>
    results.forEach((r) => r.status === 'rejected' && report('queues')(r.reason))
  );
  // Y el resto de la instancia, como red para las claves que se añadan en el
  // futuro, SALVO las tres colas: trabajo sin enviar.
  try {
    const keys = await localforage.keys();
    await Promise.all(
      keys
        .filter((key) => !QUEUE_KEYS_KEPT_ON_LOGOUT.includes(key))
        .map((key) => localforage.removeItem(key)),
    );
  } catch (error) {
    report('localforage')(error);
  }

  // ── OPFS: toda la raíz salvo las fotos pendientes de subir ────────────────
  await deleteAllOpfsEntries({ keep: [OFFLINE_PENDING_UPLOADS_DIR] }).catch(report('opfs'));

  // ── sessionStorage: todo salvo las marcas técnicas ────────────────────────
  // Clave a clave con `removeItem`, NUNCA con `clear()`: en el APK el espejo
  // (`installNativeStorageMirror`) traduce cada `removeItem` a Preferences y a
  // los borradores duraderos; `clear()` dispararía su limpieza nuclear, que ya
  // no sabría de quién son los borradores.
  try {
    Object.keys(sessionStorage)
      .filter((key) => !keptOnLogout(key))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    report('sessionStorage')(error);
  }
  // El barrido se lleva `user` a mano, sin pasar por `setSessionStorageUser`,
  // así que borra también su copia para reabrir sin cobertura, que vive en
  // `localStorage` y sobreviviría al logout: el siguiente operario de una
  // tablet compartida podría reabrir sin red con la sesión del anterior.
  try {
    clearOfflineSession();
  } catch (error) {
    report('offlineSession')(error);
  }

  // ── localStorage: el tamaño de página por tabla ───────────────────────────
  // Una clave por tabla (`rois-page-size:<tableId>`): tras el logout el
  // selector debe volver a su valor por defecto, o enseñaría «50/100» con el
  // cursor en `takeBase`. Se copia la lista antes de borrar porque
  // `localStorage` se reindexa con cada `removeItem`.
  try {
    const prefix = `${PAGE_SIZE_STORAGE_PREFIX}:`;
    Object.keys(localStorage)
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    report('localStorage')(error);
  }
}
